import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { SUPPORTED_LANGUAGES, normalizeLanguageCode } from '@/lib/i18n';

export function useLanguage() {
  const { i18n } = useTranslation();

  const changeLanguage = useCallback(async (lng: string) => {
    try {
      const normalized = normalizeLanguageCode(lng);
      await i18n.changeLanguage(normalized);
      document.documentElement.lang = normalized;
      document.documentElement.dir = i18n.dir(normalized);
      localStorage.setItem('i18nextLng', normalized);
      return true;
    } catch (error) {
      console.error('Failed to change language:', error);
      return false;
    }
  }, [i18n]);

  const normalizedCurrentLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language || 'en');

  const getCurrentLanguage = useCallback(() => {
    return (
      SUPPORTED_LANGUAGES.find((language) => language.code === normalizedCurrentLanguage) ||
      SUPPORTED_LANGUAGES.find((language) => language.code === 'en') ||
      SUPPORTED_LANGUAGES[0]
    );
  }, [normalizedCurrentLanguage]);

  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const d = new Date(date);
    return new Intl.DateTimeFormat(normalizedCurrentLanguage, options).format(d);
  }, [normalizedCurrentLanguage]);

  const formatNumber = useCallback((num: number, options?: Intl.NumberFormatOptions) => {
    return new Intl.NumberFormat(normalizedCurrentLanguage, options).format(num);
  }, [normalizedCurrentLanguage]);

  const formatCurrency = useCallback((amount: number, currency = 'USD') => {
    return new Intl.NumberFormat(normalizedCurrentLanguage, {
      style: 'currency',
      currency
    }).format(amount);
  }, [normalizedCurrentLanguage]);

  const isRTL = useCallback(() => {
    return i18n.dir(normalizedCurrentLanguage) === 'rtl';
  }, [i18n, normalizedCurrentLanguage]);

  return {
    currentLanguage: normalizedCurrentLanguage,
    language: normalizedCurrentLanguage,
    changeLanguage,
    formatDate,
    formatNumber,
    formatCurrency,
    getCurrentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    isRTL: isRTL(),
    dir: i18n.dir(normalizedCurrentLanguage)
  };
}